import axios from 'axios';
import useAppStore from '@/stores/appStore';

export const getBackendUrl = () => {
  const hostname = window.location.hostname;
  const envBackendUrl = process.env.REACT_APP_BACKEND_URL;

  // 1. Explicit override via env var (full URL) — escape hatch for unusual deployments.
  if (envBackendUrl && (envBackendUrl.startsWith('https://') || envBackendUrl.startsWith('http://'))) {
    return envBackendUrl;
  }

  // 2. Local dev server (npm start) talks directly to the backend container.
  if (process.env.NODE_ENV === 'development' && (hostname === 'localhost' || hostname === '127.0.0.1')) {
    return envBackendUrl || 'http://localhost:8000';
  }

  // 3. Every other domain (any client's own custom domain): same-origin relative path.
  // Nginx proxies /api/ to the backend container (see frontend/nginx.conf), and the
  // backend resolves the tenant from the Host header — no per-domain frontend config needed.
  return '';
};

export const BACKEND_URL = getBackendUrl();
export const API_BASE = `${BACKEND_URL}/api/v1`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and tenant header
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Local dev has no real per-client Host header for the backend to derive a tenant
    // from, so send an explicit hint. In production the backend resolves the tenant from
    // the request's own Host header (see TenantContextMiddleware) — the same domain the
    // browser is actually on — so no client-specific config is needed here.
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      const urlParams = new URLSearchParams(window.location.search);
      config.headers['X-Tenant'] = urlParams.get('tenant') || process.env.REACT_APP_DEFAULT_TENANT || 'kwicc';
    }


    // Inject X-Impersonate-User-Id header if viewAsUser is set
    try {
      const viewAsUser = useAppStore.getState().viewAsUser;
      if (viewAsUser && viewAsUser.id) {
        const isProfileCall = config.url.endsWith('/auth/me') || config.url.includes('/auth/me?') || config.url === 'auth/me';
        const isAnalyticsCall = config.url.includes('/analytics/');
        if (!isProfileCall && !isAnalyticsCall) {
          config.headers['X-Impersonate-User-Id'] = viewAsUser.id;
        }
      }
    } catch (e) {
      // Ignore if appStore isn't initialized yet
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      const isOpsRoute = window.location.pathname.startsWith('/ops');
      window.location.href = isOpsRoute ? '/ops/login' : '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authApi = {
  requestOtp: (phone, portal) => api.post('/auth/otp/request', { phone, portal }),
  verifyOtp: (phone, otp, portal) => api.post('/auth/otp/verify', { phone, otp, portal }),
  login: (data) => api.post('/auth/login', data),
  googleLogin: (idToken, portal) => api.post('/auth/google', { id_token: idToken, portal }),
  register: (data) => api.post('/auth/register', data),
  verifyRegistration: (phone, otp) => api.post('/auth/register/verify', { phone, otp }),
  registerComplete: (data) => api.post('/auth/register/complete', data),
  refreshToken: () => api.post('/auth/refresh'),
  getCurrentUser: () => api.get('/auth/me'),
  updateMe: (data) => api.patch('/auth/me', data),
  requestPasswordReset: (phone) => api.post('/auth/password/reset/request', { phone }),
  confirmPasswordReset: (phone, otp, newPassword) => api.post('/auth/password/reset/confirm', { phone, otp, new_password: newPassword }),
};

// Tickets API
export const ticketsApi = {
  list: (params) => api.get('/tickets', { params }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.patch(`/tickets/${id}`, data),
  approve: (id) => api.post(`/tickets/${id}/approve`),
  pickup: (id) => api.post(`/tickets/${id}/pickup`),
  reject: (id, reason) => api.post(`/tickets/${id}/reject`, { reason }),
  assign: (id, techId) => api.post(`/tickets/${id}/assign`, { tech_id: techId }),
  getEvents: (id) => api.get(`/tickets/${id}/events`),
  addNote: (id, note) => api.post(`/tickets/${id}/notes`, { note }),
  addComment: (id, note) => api.post(`/tickets/${id}/notes`, { note }),
  close: (id) => api.post(`/tickets/${id}/close`),
  rate: (id, ratingData) => api.post(`/tickets/${id}/rate`, ratingData),
  reassign: (id, data) => api.post(`/tickets/${id}/reassign`, data),
};

// Products API
export const productsApi = {
  getCategories: () => api.get('/products/categories'),
  getIssues: (categoryId) => api.get(`/products/categories/${categoryId}/issues`),
  register: (data) => api.post('/products/register', data),
  getBySerial: (serial) => api.get(`/products/serial/${serial}`),
  getMy: () => api.get('/products/my'),
  getCustomerProducts: (customerId) => api.get(`/products/customer/${customerId}`),
  registerForCustomer: (customerId, serialNumber) => api.post('/products/register-for-customer', { customer_id: customerId, serial_number: serialNumber }),
  unregisterProduct: (registrationId) => api.delete(`/products/registrations/${registrationId}`),
  listGlobal: () => api.get('/products'),
  createGlobal: (data) => api.post('/products', data),
  updateGlobal: (id, data) => api.patch(`/products/${id}`, data),
  deleteGlobal: (id) => api.delete(`/products/${id}`),
  // Models CRUD
  listModels: () => api.get('/products/models'),
  createModel: (data) => api.post('/products/models', data),
  updateModel: (id, data) => api.patch(`/products/models/${id}`, data),
  deleteModel: (id) => api.delete(`/products/models/${id}`),
  // Batch Operations
  batchValidate: (data) => api.post('/products/batch-validate', data),
  batchCreate: (data) => api.post('/products/batch-create', data),
  batchDelete: (data) => api.post('/products/batch-delete', data),
  getRegistrationReport: () => api.get('/analytics/product-registrations'),
};

// Jobs API
export const jobsApi = {
  getMy: () => api.get('/jobs/my'),
  get: (id) => api.get(`/jobs/${id}`),
  markNavigating: (id) => api.post(`/jobs/${id}/navigating`),
  markArrived: (id, gps) => api.post(`/jobs/${id}/arrived`, gps),
  verifyStartOtp: (id, otp) => api.post(`/jobs/${id}/verify-start-otp`, { otp }),
  submitChecklist: (id, items) => api.post(`/jobs/${id}/checklist`, { items }),
  recordPayment: (id, payment) => api.post(`/jobs/${id}/payment`, payment),
  deletePayment: (id) => api.delete(`/jobs/${id}/payment`),
  markComplete: (id, data) => {
    const headers = {};
    if (data?.gps?.lat !== undefined && data?.gps?.lat !== null) {
      headers['X-GPS-Lat'] = data.gps.lat.toString();
    }
    if (data?.gps?.lng !== undefined && data?.gps?.lng !== null) {
      headers['X-GPS-Lng'] = data.gps.lng.toString();
    }
    return api.post(`/jobs/${id}/complete`, { fault_notes: data.fault_notes, photos: data.photos }, { headers });
  },
  verifyCloseOtp: (id, otp) => api.post(`/jobs/${id}/verify-close-otp`, { otp }),
  rate: (id, rating) => api.post(`/jobs/${id}/rate`, rating),
  resendOtp: (id) => api.post(`/jobs/${id}/resend-otp`),
  resendStartOtp: (id) => api.post(`/jobs/${id}/resend-start-otp`),
};

// Users API
export const usersApi = {
  list: (params) => api.get('/users', { params }),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getStaffReviews: () => api.get('/users/staff/reviews'),
};

// Analytics API
export const analyticsApi = {
  getOverview: () => api.get('/analytics/overview'),
  getCharts: () => api.get('/analytics/charts'),
  getAuditLogs: (params) => api.get('/analytics/audit-logs', { params }),
  getStaffReports: (params) => api.get('/analytics/reports/staff', { params }),
  getStaffReportDetail: (id, params) => api.get(`/analytics/reports/staff/${id}`, { params }),
  getProductRegistrations: () => api.get('/analytics/product-registrations'),
};

// WhatsApp API
export const whatsappApi = {
  getStatus: () => api.get('/whatsapp/status'),
  connect: () => api.post('/whatsapp/connect'),
  logout: () => api.post('/whatsapp/logout'),
  sendTest: (phone, message) => api.post('/whatsapp/test', { phone, message }),
  getLogs: () => api.get('/whatsapp/logs'),
  simulate: (phone, message) => api.post('/whatsapp/simulate', { phone, message }),
};

// WhatsApp live-agent inbox (handoff)
export const waInboxApi = {
  list: (params = {}) => api.get('/whatsapp/inbox/conversations', { params }),
  thread: (id) => api.get(`/whatsapp/inbox/conversations/${id}`),
  reply: (id, message) => api.post(`/whatsapp/inbox/conversations/${id}/reply`, { message }),
  retryMessage: (id, messageId) => api.post(`/whatsapp/inbox/conversations/${id}/messages/${messageId}/retry`),
  setMode: (id, mode) => api.post(`/whatsapp/inbox/conversations/${id}/mode`, { mode }),
  claim: (id) => api.post(`/whatsapp/inbox/conversations/${id}/claim`),
  close: (id) => api.post(`/whatsapp/inbox/conversations/${id}/close`),
};

// WhatsApp broadcast / contacts (marketing)
export const broadcastApi = {
  // Contacts
  listContacts: (params = {}) => api.get('/marketing/contacts', { params }),
  createContact: (data) => api.post('/marketing/contacts', data),
  updateContact: (id, data) => api.patch(`/marketing/contacts/${id}`, data),
  deleteContact: (id) => api.delete(`/marketing/contacts/${id}`),
  syncCustomers: () => api.post('/marketing/contacts/sync-customers'),
  tags: () => api.get('/marketing/contacts/tags'),
  importContacts: (formData) => api.post('/marketing/contacts/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportContacts: () => api.get('/marketing/contacts/export', { responseType: 'blob' }),
  // Campaigns
  listCampaigns: (params = {}) => api.get('/marketing/campaigns', { params }),
  createCampaign: (data) => api.post('/marketing/campaigns', data),
  getCampaign: (id) => api.get(`/marketing/campaigns/${id}`),
  updateCampaign: (id, data) => api.patch(`/marketing/campaigns/${id}`, data),
  sendCampaign: (id) => api.post(`/marketing/campaigns/${id}/send`),
  deleteCampaign: (id) => api.delete(`/marketing/campaigns/${id}`),
};

// Notifications API
export const notificationsApi = {
  list: () => api.get('/notifications'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

// Upload API
export const uploadApi = {
  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data.url;
  },
};

// Tenants API
export const tenantsApi = {
  get: (slug) => api.get(`/tenants/${slug}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.patch(`/tenants/${id}`, data),
  testEmail: (id, recipient) => api.post(`/tenants/${id}/test-email`, { recipient }),
};

// Categories API (Admin CRUD)
export const categoriesApi = {
  list: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.patch(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  // Issue categories
  listIssues: (categoryId) => api.get(`/categories/${categoryId}/issues`),
  createIssue: (categoryId, data) => api.post(`/categories/${categoryId}/issues`, data),
  updateIssue: (id, data) => api.patch(`/categories/issues/${id}`, data),
  deleteIssue: (id) => api.delete(`/categories/issues/${id}`),
};

// Checklists API (Admin CRUD)
export const checklistsApi = {
  list: () => api.get('/categories/checklists'),
  create: (data) => api.post('/categories/checklists', data),
  update: (id, data) => api.patch(`/categories/checklists/${id}`, data),
  delete: (id) => api.delete(`/categories/checklists/${id}`),
  // Checklist items
  createItem: (checklistId, data) => api.post(`/categories/checklists/${checklistId}/items`, data),
  updateItem: (id, data) => api.patch(`/categories/checklist-items/${id}`, data),
  deleteItem: (id) => api.delete(`/categories/checklist-items/${id}`),
};

// Backups API (Admin CRUD)
export const backupsApi = {
  list: () => api.get('/backups'),
  trigger: () => api.post('/backups/trigger'),
  getDownloadUrl: (fileKey) => api.get(`/backups/download/${encodeURIComponent(fileKey)}`),
  delete: (id) => api.delete(`/backups/${id}`),
  testConnection: (data) => api.post('/backups/test-connection', data),
  restore: (id) => api.post(`/backups/restore/${id}`),
  resetDb: () => api.post('/backups/reset-db'),
};
